/**
 * Session expirée / token invalide : réponses 401 ou 403 sur les appels authentifiés
 * → nettoyage du stockage et redirection vers /login.
 * La route POST /auth/login est exclue pour ne pas rediriger sur un mauvais mot de passe.
 */
import axios from "axios";
import { clearAllAuthTokens } from "./utils/authStorage";

let redirecting = false;
let installed = false;

function shouldIgnoreAuthErrorUrl(url) {
  if (!url || typeof url !== "string") return false;
  return /\/auth\/login/i.test(url);
}

export function clearAuthAndRedirectToLogin() {
  if (typeof window === "undefined") return;
  const path = window.location.pathname || "";
  if (path === "/login" || path.startsWith("/login/")) return;
  if (redirecting) return;
  redirecting = true;
  clearAllAuthTokens();
  try {
    delete axios.defaults.headers.common?.Authorization;
  } catch {
    /* ignore */
  }
  window.location.replace("/login");
}

function readAxiosAuthorization(config) {
  if (!config?.headers) return null;
  const h = config.headers;
  if (typeof h.get === "function") {
    return h.get("Authorization") || h.get("authorization");
  }
  return h.Authorization ?? h.authorization ?? null;
}

/** Requête axios envoyée avec un Bearer (config ou defaults après login). */
function axiosRequestHadBearer(config) {
  let auth = readAxiosAuthorization(config);
  if (!auth) {
    try {
      auth = axios.defaults.headers.common?.Authorization;
    } catch {
      /* ignore */
    }
  }
  return !!(auth && String(auth).trim().toLowerCase().startsWith("bearer"));
}

function hasBearerAuth(input, init) {
  const headers = init?.headers;
  if (headers) {
    if (typeof Headers !== "undefined" && headers instanceof Headers) {
      const a = headers.get("Authorization") || headers.get("authorization");
      if (a && String(a).trim().toLowerCase().startsWith("bearer")) return true;
    } else if (headers && typeof headers === "object") {
      for (const k of Object.keys(headers)) {
        if (k.toLowerCase() === "authorization") {
          const v = headers[k];
          if (v && String(v).trim().toLowerCase().startsWith("bearer")) return true;
        }
      }
    }
  }
  if (typeof Request !== "undefined" && input instanceof Request) {
    const a = input.headers.get("Authorization") || input.headers.get("authorization");
    if (a && String(a).trim().toLowerCase().startsWith("bearer")) return true;
  }
  return false;
}

function resolveRequestUrl(input) {
  if (typeof input === "string") return input;
  if (typeof URL !== "undefined" && input instanceof URL) return input.href;
  if (input && typeof input === "object" && typeof input.url === "string") return input.url;
  return "";
}

function installFetchGuard() {
  if (typeof window === "undefined" || window.__ecrFetchAuthGuardInstalled) return;
  window.__ecrFetchAuthGuardInstalled = true;
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async function patchedFetch(input, init) {
    const res = await nativeFetch(input, init);
    if ((res.status === 401 || res.status === 403) && hasBearerAuth(input, init)) {
      const url = resolveRequestUrl(input);
      if (!shouldIgnoreAuthErrorUrl(url)) {
        clearAuthAndRedirectToLogin();
      }
    }
    return res;
  };
}

function installAxiosInterceptors() {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;
      const url = error.config?.url || "";
      if (
        (status === 401 || status === 403) &&
        axiosRequestHadBearer(error.config) &&
        !shouldIgnoreAuthErrorUrl(url)
      ) {
        clearAuthAndRedirectToLogin();
      }
      return Promise.reject(error);
    }
  );
}

function install() {
  if (installed) return;
  installed = true;
  installAxiosInterceptors();
  installFetchGuard();
}

install();
